import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import { generateBuildingInterior } from "@cold-case/shared/interior-layout";
import type { BuildingInteriorSpec } from "@cold-case/shared/interior-layout";

import type { CoherenceJournal } from "../lib/dialog-parser.js";
import {
  resolveBuildingInvestigation,
  VACANT_BUILDING_NARRATIVE,
} from "../lib/building-investigation.js";

export type BuildingExitStand = {
  wx: number;
  wy: number;
  outdoorZone: "downtown" | "maple_heights";
  footLeft: number;
  footTop: number;
  footSpan: number;
  spineWx: number;
  spineWy: number;
};

export type InteriorContentPlan = {
  spec: BuildingInteriorSpec;
  /** Show neon triangle — investigate on interact. */
  showClueMarker: boolean;
  /** NPC instance_id placed inside instead of triangle. */
  interiorNpcInstanceId?: string;
};

export function npcForBuildingInterior(
  manifest: CaseManifestV1,
  buildingId: string
): string | undefined {
  const n = manifest.npcs.find(
    (x) => x.position_near_building?.trim() === buildingId
  );
  return n?.instance_id;
}

export function shouldShowClueMarker(
  manifest: CaseManifestV1,
  buildingId: string,
  journal: CoherenceJournal,
  decor: boolean,
  opts?: { userId?: string; playerCount?: number }
): boolean {
  if (decor) return false;
  const b = manifest.buildings.find((x) => x.building_id === buildingId);
  if (!b) return false;
  const res = resolveBuildingInvestigation(b, journal, { manifest, ...opts });
  if (!res.permitted) return false;
  if (res.clueIdsToReveal.length > 0) return true;
  const inv = b.investigation;
  if (!inv) return false;
  if (inv.default_result.trim() === VACANT_BUILDING_NARRATIVE) return false;
  if (inv.default_result.trim() && inv.default_result.trim() !== "You find nothing notable.") {
    return true;
  }
  return (inv.conditional_results?.length ?? 0) > 0;
}

export function buildInteriorContentPlan(
  seedId: string,
  manifest: CaseManifestV1,
  buildingId: string,
  journal: CoherenceJournal,
  decor: boolean,
  opts?: { userId?: string; playerCount?: number }
): InteriorContentPlan {
  const interiorNpcInstanceId = decor ? undefined : npcForBuildingInterior(manifest, buildingId);
  const showClueMarker = shouldShowClueMarker(manifest, buildingId, journal, decor, opts);
  const spec = generateBuildingInterior({
    seedId,
    buildingId,
    hasContent: showClueMarker,
    hasNpc: !!interiorNpcInstanceId,
  });
  return { spec, showClueMarker, interiorNpcInstanceId };
}
